import { Injectable } from '@angular/core';
import { Faq, IFaq } from '../models/faq.model';
import { AuthService } from './auth.service';
import { HttpClient } from '@angular/common/http';
import { SpinnerService } from './spinner.service';
import { ToastService } from './toast.service';
import { environment } from '../../environments/environment';
import { IServerResponse, ServerResponse } from '../models/server-response.model';
import { FaqGroup } from '../models/faq-group.model';

/* Cuerpo exacto de FaqOrderRequest: SIEMPRE la lista completa de ids del grupo
   en el orden deseado — la posición dentro del arreglo es el sortOrder. Una
   pregunta no puede cambiar de grupo por esta vía: si llega un id que no
   pertenece al grupo, el backend responde 400. */
export interface IFaqOrderRequest {
  faqIds: string[];
}

/* Cuerpo exacto de FaqRequest. videoUrl es nullable en base de datos, pero se
   envía SIEMPRE: si el PUT lo omite, el backend lo pone en null y borra el que
   la pregunta ya tuviera. */
export interface IFaqRequest {
  question: string;
  answer: string;
  videoUrl: string | null;
  status: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class FaqService {

  public loading: boolean = false;
  public faqs: Faq[] = [];

  constructor(
    public authService: AuthService,
    private http: HttpClient,
    public spinnerService: SpinnerService,
    public toastService: ToastService
  ) {
  }

  /*
   ██████╗██████╗ ███████╗ █████╗ ████████╗███████╗
  ██╔════╝██╔══██╗██╔════╝██╔══██╗╚══██╔══╝██╔════╝
  ██║     ██████╔╝█████╗  ███████║   ██║   █████╗
  ██║     ██╔══██╗██╔══╝  ██╔══██║   ██║   ██╔══╝
  ╚██████╗██║  ██║███████╗██║  ██║   ██║   ███████╗
   ╚═════╝╚═╝  ╚═╝╚══════╝╚═╝  ╚═╝   ╚═╝   ╚══════╝

  */
  async create(
    faqGroup: FaqGroup,
    faq: IFaqRequest
  ): Promise<boolean> {

    let flag = false;

    const urlApi = `${environment.urlApi}/platforms/${faqGroup.platformId}/faq-groups/${faqGroup.faqGroupId}/faqs`;

    this.spinnerService.loading = true;
    this.spinnerService.message = 'Registrando pregunta frecuente';

    try {
      const token = await this.authService.getToken();
      const response = await new Promise<ServerResponse>(
        (resolve, reject) => {
          this.http.post<IServerResponse>(
            urlApi,
            JSON.stringify(faq),
            {
              headers: {
                "content-type": "application/json",
                'Authorization': token.toString()
              }
            }
          ).subscribe({
            next: (response: IServerResponse) => resolve(new ServerResponse(response)),
            error: (error) => {
              if (error.error) {
                resolve(new ServerResponse(error.error));
              } else {
                reject(error);
                this.toastService.show({ header: 'Error de servidor', body: `${error}`, classname: 'bg-danger text-white' });
              }
            },
          });
        }
      );

      if(response.statusCode === 201) {
        flag = true;
        this.toastService.show({ body: `${response.message}`, classname: 'bg-success text-white' });
      } else {
        this.toastService.show({ header: 'Error de servidor', body: `${response.message}`, classname: 'bg-danger text-white' });
      }

    } catch(error) {
      console.log(error);
      this.toastService.show({ header: 'Error', body: `${error}`, classname: 'bg-danger text-white' });
    }

    this.spinnerService.loading = false;
    this.spinnerService.message = '';

    return flag;
  }

  /*
  ██████╗ ███████╗██╗     ███████╗████████╗███████╗
  ██╔══██╗██╔════╝██║     ██╔════╝╚══██╔══╝██╔════╝
  ██║  ██║█████╗  ██║     █████╗     ██║   █████╗
  ██║  ██║██╔══╝  ██║     ██╔══╝     ██║   ██╔══╝
  ██████╔╝███████╗███████╗███████╗   ██║   ███████╗
  ╚═════╝ ╚══════╝╚══════╝╚══════╝   ╚═╝   ╚══════╝

  */
  async delete(
    faqGroup: FaqGroup,
    faq: Faq
  ): Promise<boolean> {

    let flag = false;

    const urlApi = `${environment.urlApi}/platforms/${faqGroup.platformId}/faq-groups/${faqGroup.faqGroupId}/faqs/${faq.faqId}`;

    this.spinnerService.loading = true;
    this.spinnerService.message = 'Eliminando pregunta frecuente';

    try {
      const token = await this.authService.getToken();
      const response = await new Promise<ServerResponse>(
        (resolve, reject) => {
          this.http.delete<IServerResponse>(
            urlApi,
            { headers: { 'Authorization': token.toString() } }
          ).subscribe({
            next: (response: IServerResponse) => resolve(new ServerResponse(response)),
            error: (error) => {
              if (error.error) {
                resolve(new ServerResponse(error.error));
              } else {
                reject(error);
                this.toastService.show({ header: 'Error de servidor', body: `${error}`, classname: 'bg-danger text-white' });
              }
            },
          });
        }
      );

      if(response.statusCode === 200) {
        flag = true;
        this.toastService.show({ body: `${response.message}`, classname: 'bg-success text-white' });
      } else {
        this.toastService.show({ header: 'Error de servidor', body: `${response.message}`, classname: 'bg-danger text-white' });
      }

    } catch(error) {
      console.log(error);
      this.toastService.show({ header: 'Error', body: `${error}`, classname: 'bg-danger text-white' });
    }

    this.spinnerService.loading = false;
    this.spinnerService.message = '';

    return flag;
  }

  /*
   ██████╗ ███████╗████████╗
  ██╔════╝ ██╔════╝╚══██╔══╝
  ██║  ███╗█████╗     ██║
  ██║   ██║██╔══╝     ██║
  ╚██████╔╝███████╗   ██║
   ╚═════╝ ╚══════╝   ╚═╝

  */
  async get(
    faqGroup: FaqGroup,
    faqId: string
  ): Promise<Faq | null> {

    let faq: Faq | null = null;

    const urlApi = `${environment.urlApi}/platforms/${faqGroup.platformId}/faq-groups/${faqGroup.faqGroupId}/faqs/${faqId}`;

    this.spinnerService.loading = true;
    this.spinnerService.message = 'Obteniendo pregunta frecuente';

    try {
      const token = await this.authService.getToken();
      const response = await new Promise<ServerResponse<{ faq: IFaq }>>(
        (resolve, reject) => {
          this.http.get<IServerResponse<{ faq: IFaq }>>(
            urlApi,
            { headers: { "content-type": "application/json", "Authorization": token.toString() } }
          ).subscribe({
            next: (response: IServerResponse<{ faq: IFaq }>) => resolve(new ServerResponse<{ faq: IFaq }>(response)),
            error: (error) => {
              if (error.error) {
                resolve(new ServerResponse(error.error));
              } else {
                reject(error);
              }
            },
          });
        }
      );

      if(response.statusCode === 200) {
        faq = new Faq(response.data!.faq);
      } else {
        this.toastService.show({ header: 'Error de servidor', body: `${response.message}`, classname: 'bg-danger text-white' });
      }

    } catch(error) {
      console.log(error);
    }

    this.spinnerService.loading = false;
    this.spinnerService.message = '';

    return faq;
  }

  /*
   ██████╗ ███████╗████████╗ █████╗ ██╗     ██╗
  ██╔════╝ ██╔════╝╚══██╔══╝██╔══██╗██║     ██║
  ██║  ███╗█████╗     ██║   ███████║██║     ██║
  ██║   ██║██╔══╝     ██║   ██╔══██║██║     ██║
  ╚██████╔╝███████╗   ██║   ██║  ██║███████╗███████╗
   ╚═════╝ ╚══════╝   ╚═╝   ╚═╝  ╚═╝╚══════╝╚══════╝

  */
  async getAll(
    faqGroup: FaqGroup
  ) {

    this.loading = true;

    const urlApi = `${environment.urlApi}/platforms/${faqGroup.platformId}/faq-groups/${faqGroup.faqGroupId}/faqs`;

    try {
      const token = await this.authService.getToken();
      const response = await new Promise<ServerResponse<{ faqs: IFaq[] }>>(
        (resolve, reject) => {
          this.http.get<IServerResponse<{ faqs: IFaq[] }>>(
            urlApi,
            { headers: { "content-type": "application/json", "Authorization": token.toString() } }
          ).subscribe({
            next: (response: IServerResponse<{ faqs: IFaq[] }>) => resolve(new ServerResponse<{ faqs: IFaq[] }>(response)),
            error: (error) => {
              if (error.error) {
                resolve(new ServerResponse(error.error));
              } else {
                reject(error);
              }
            },
          });
        }
      );

      if(response.statusCode === 200) {
        this.faqs = response.data!.faqs.map(faq => new Faq(faq));
      } else {
        this.toastService.show({ header: 'Error de servidor', body: `${response.message}`, classname: 'bg-danger text-white' });
      }

    } catch(error) {
      console.log(error);
    }

    this.loading = false;
  }

  /*
  ██╗   ██╗██████╗ ██████╗  █████╗ ████████╗███████╗
  ██║   ██║██╔══██╗██╔══██╗██╔══██╗╚══██╔══╝██╔════╝
  ██║   ██║██████╔╝██║  ██║███████║   ██║   █████╗
  ██║   ██║██╔═══╝ ██║  ██║██╔══██║   ██║   ██╔══╝
  ╚██████╔╝██║     ██████╔╝██║  ██║   ██║   ███████╗
   ╚═════╝ ╚═╝     ╚═════╝ ╚═╝  ╚═╝   ╚═╝   ╚══════╝

  */
  async update(
    faqGroup: FaqGroup,
    faqId: string,
    faq: IFaqRequest
  ): Promise<boolean> {

    let flag = false;

    const urlApi = `${environment.urlApi}/platforms/${faqGroup.platformId}/faq-groups/${faqGroup.faqGroupId}/faqs/${faqId}`;

    this.spinnerService.loading = true;
    this.spinnerService.message = 'Actualizando pregunta frecuente';

    try {
      const token = await this.authService.getToken();
      const response = await new Promise<ServerResponse>(
        (resolve, reject) => {
          this.http.put<IServerResponse>(
            urlApi,
            JSON.stringify(faq),
            { headers: { "content-type": "application/json", 'Authorization': token.toString() } }
          ).subscribe({
            next: (response: IServerResponse) => resolve(new ServerResponse(response)),
            error: (error) => {
              if (error.error) {
                resolve(new ServerResponse(error.error));
              } else {
                reject(error);
                this.toastService.show({ header: 'Error de servidor', body: `${error}`, classname: 'bg-danger text-white' });
              }
            },
          });
        }
      );

      if(response.statusCode === 200) {
        flag = true;
        this.toastService.show({ body: `${response.message}`, classname: 'bg-success text-white' });
      } else {
        /* Faltaba en el original: sin este else un 4xx/5xx se tragaba en
           silencio y el modal parecía haber guardado. */
        this.toastService.show({ header: 'Error de servidor', body: `${response.message}`, classname: 'bg-danger text-white' });
      }

    } catch(error) {
      console.log(error);
      this.toastService.show({ header: 'Error', body: `${error}`, classname: 'bg-danger text-white' });
    }

    this.spinnerService.loading = false;
    this.spinnerService.message = '';

    return flag;
  }

  /*
  ██╗   ██╗██████╗ ██████╗  █████╗ ████████╗███████╗ ██████╗ ██████╗ ██████╗ ███████╗██████╗
  ██║   ██║██╔══██╗██╔══██╗██╔══██╗╚══██╔══╝██╔════╝██╔═══██╗██╔══██╗██╔══██╗██╔════╝██╔══██╗
  ██║   ██║██████╔╝██║  ██║███████║   ██║   █████╗  ██║   ██║██████╔╝██║  ██║█████╗  ██████╔╝
  ██║   ██║██╔═══╝ ██║  ██║██╔══██║   ██║   ██╔══╝  ██║   ██║██╔══██╗██║  ██║██╔══╝  ██╔══██╗
  ╚██████╔╝██║     ██████╔╝██║  ██║   ██║   ███████╗╚██████╔╝██║  ██║██████╔╝███████╗██║  ██║
   ╚═════╝ ╚═╝     ╚═════╝ ╚═╝  ╚═╝   ╚═╝   ╚══════╝ ╚═════╝ ╚═╝  ╚═╝╚═════╝ ╚══════╝╚═╝  ╚═╝

  */
  /* A propósito NO levanta el spinnerService: reordenar es una acción de alta
     frecuencia y el overlay global bloqueando toda la pantalla en cada
     movimiento la vuelve inusable. El indicador de guardado va en la pieza
     afectada, dentro de la pantalla.

     Tampoco muestra toast en el camino feliz — el resultado ya se ve en la
     lista. El toast se reserva para el error, que es lo único que el usuario
     no puede deducir mirando. */
  async updateOrder(
    faqGroup: FaqGroup,
    faqIds: string[]
  ): Promise<Faq[] | null> {

    let faqs: Faq[] | null = null;

    const urlApi = `${environment.urlApi}/platforms/${faqGroup.platformId}/faq-groups/${faqGroup.faqGroupId}/faqs/order`;

    const body: IFaqOrderRequest = { faqIds: faqIds };

    try {
      const token = await this.authService.getToken();
      const response = await new Promise<ServerResponse<{ faqs: IFaq[] }>>(
        (resolve, reject) => {
          this.http.put<IServerResponse<{ faqs: IFaq[] }>>(
            urlApi,
            JSON.stringify(body),
            { headers: { "content-type": "application/json", 'Authorization': token.toString() } }
          ).subscribe({
            next: (response: IServerResponse<{ faqs: IFaq[] }>) => resolve(new ServerResponse<{ faqs: IFaq[] }>(response)),
            error: (error) => {
              if (error.error) {
                resolve(new ServerResponse(error.error));
              } else {
                reject(error);
                this.toastService.show({ header: 'Error de servidor', body: `${error}`, classname: 'bg-danger text-white' });
              }
            },
          });
        }
      );

      if(response.statusCode === 200) {
        /* El endpoint devuelve la colección completa ya reordenada: se regresa
           tal cual para que la pantalla refresque los sortOrder sin volver a
           pedir el árbol. */
        faqs = response.data!.faqs.map(faq => new Faq(faq));
      } else {
        /* El 400 del backend ya viene redactado en español para el usuario. */
        this.toastService.show({ header: 'No se pudo guardar el orden', body: `${response.message}`, classname: 'bg-danger text-white' });
      }

    } catch(error) {
      console.log(error);
      this.toastService.show({ header: 'Error', body: `${error}`, classname: 'bg-danger text-white' });
    }

    return faqs;
  }
}
